import _extends from '../../../node_modules/@babel/runtime/helpers/extends.js';
import _defineProperty from '../../../node_modules/@babel/runtime/helpers/defineProperty.js';
import _slicedToArray from '../../../node_modules/@babel/runtime/helpers/slicedToArray.js';
import _objectWithoutProperties from '../../../node_modules/@babel/runtime/helpers/objectWithoutProperties.js';
import React, { useState, useCallback, useEffect } from 'react';
import 'prop-types';
import { jsx } from '@emotion/core';
import getPreferredValue from '../../utils/getPreferredValue.js';
import useEscape from '../../utils/useEscape.js';
import useResize from '../../utils/useResize.js';
import tooltipStyleObject from '../../styles/tooltip.js';
import useClickOutside from '../../utils/useClickOutside.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var tooltip = tooltipStyleObject.tooltip,
    tooltipContainer = tooltipStyleObject.tooltipContainer,
    tooltipAnimIn = tooltipStyleObject.tooltip_animIn,
    tooltipAnimInTooltipInner = tooltipStyleObject.tooltip_animIn__tooltipInner,
    tooltipAnimInTooltipArrowPointer = tooltipStyleObject.tooltip_animIn__tooltipArrowPointer,
    tooltipInner = tooltipStyleObject.tooltipInner,
    tooltipArrowPointer = tooltipStyleObject.tooltipArrowPointer,
    tooltipInfo = tooltipStyleObject.tooltipInfo,
    tooltipInfoInner = tooltipStyleObject.tooltipInfo__tooltipInner;
var notTooltipInfoAndTooltipInner = tooltip['&:not(.tooltipInfo) .tooltipInner'];
/**
 * Returns an array of css styles specific to the provided placement directionality
 *
 * @param {string} placement - the original directionality of the tooltip* @param placement
 * @returns {array} an array of css style objects specific to the placement directionality
 */

function getPlacementStyles(placement) {
  return [tooltipStyleObject["tooltip_data_placement_".concat(placement)], tooltipStyleObject["tooltip_data_placement_".concat(placement, "___tooltipArrow")], tooltipStyleObject["tooltip_data_placement_".concat(placement, "___tooltipArrowPointer")]];
}

function Tooltip(_ref) {
  var theme = _ref.theme,
      id = _ref.id,
      info = _ref.info,
      triggerElement = _ref.triggerElement,
      forcePlacement = _ref.forcePlacement,
      placement = _ref.placement,
      open = _ref.open,
      children = _ref.children,
      otherProps = _objectWithoutProperties(_ref, ["theme", "id", "info", "triggerElement", "forcePlacement", "placement", "open", "children"]);

  var tooltipRef = React.useRef(null);
  var tooltipOuterRef = React.useRef(null);

  var _useState = useState(placement),
      _useState2 = _slicedToArray(_useState, 2),
      placementState = _useState2[0],
      setPlacementState = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      offsetCss = _useState4[0],
      setOffsetCss = _useState4[1];

  var _useState5 = useState(open),
      _useState6 = _slicedToArray(_useState5, 2),
      openState = _useState6[0],
      setOpenState = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      managed = _useState8[0],
      setManaged = _useState8[1];
  /**
   * Checks the tooltip if overflowing to left
   */


  function isLeftOverflow(tooltipRect, buttonRect) {
    return tooltipRect.left <= 0 || buttonRect.left < tooltipRect.width + 20;
  }
  /**
   * Checks the tooltip if overflowing to right
   */


  function isRightOverflow(windowInnerWidth, tooltipRect, buttonRect) {
    return windowInnerWidth <= tooltipRect.right || windowInnerWidth < buttonRect.right + tooltipRect.width + 20;
  }
  /**
   * Checks the tooltip if overflowing to top
   */


  function isTopOverflow(tooltipRect, buttonRect) {
    return tooltipRect.top <= 0 || buttonRect.top < tooltipRect.height + 20;
  }
  /**
   * Checks the tooltip if overflowing to bottom
   */


  function isBottomOverflow(windowInnerHeight, tooltipRect, buttonRect) {
    return windowInnerHeight <= tooltipRect.bottom || windowInnerHeight < buttonRect.bottom + tooltipRect.height + 20;
  }
  /**
   * Determine if the tooltip can be displayed to top or bottom
   * returns
   *    top if overflows to bottom
   *    bottom if overflows to top
   *    else placement
   */
  // eslint-disable-next-line max-params


  function determineVerticalSide(innerHeight, tooltipRect, buttonRect, anyPlacement) {
    if ((anyPlacement || placement === 'top') && isTopOverflow(tooltipRect, buttonRect)) {
      return 'bottom';
    }

    if ((anyPlacement || placement === 'bottom') && isBottomOverflow(innerHeight, tooltipRect, buttonRect)) {
      return 'top';
    }

    return placement;
  }
  /**
   * Determine if the tooltip can be displayed to top or bottom
   * returns
   *    left if overflows to right
   *    right if overflows to left
   *    else placement
   */
  // eslint-disable-next-line max-params


  function determineHorizontalSide(innerWidth, tooltipRect, buttonRect, anyPlacement) {
    if ((anyPlacement || placement === 'left') && isLeftOverflow(tooltipRect, buttonRect)) {
      return 'right';
    }

    if ((anyPlacement || placement === 'right') && isRightOverflow(innerWidth, tooltipRect, buttonRect)) {
      return 'left';
    }

    return placement;
  }
  /**
   * Sets the tooltip placement based on the window and intended placement
   * 1. If forcePlacement is true, no alignment will be done.
   * 2. nothing is done if container / page size is smaller than tooltip + button.
   * 3. if expected plcement is left or right
        first check if there is enough page width to display to left or right
        if not enough width, it will be to top or bottom
   * 4. if expected plcement is top or bottom
        first check if there is enough page height to display to top or bottom
        if not enough height, it will be to left or right
   */


  function alignForWindowOverflow() {
    if (forcePlacement) {
      setPlacementState(placement);
      return placement;
    }

    var currentTooltip = tooltipRef.current;
    var buttonRect = currentTooltip.previousSibling.getBoundingClientRect();
    var tooltipRect = currentTooltip.getBoundingClientRect();
    var _window = window,
        innerWidth = _window.innerWidth,
        outerWidth = _window.outerWidth,
        innerHeight = _window.innerHeight,
        outerHeight = _window.outerHeight;
    var minimumRequiredWidth = buttonRect.width + 20 + tooltipRect.width;
    var minimumRequiredHeight = buttonRect.height + 20 + tooltipRect.height;

    if (outerWidth <= minimumRequiredWidth && outerHeight <= minimumRequiredHeight) {
      return placementState;
    }

    var placementUpdate = null;

    if (placement === 'left' || placement === 'right') {
      if (outerWidth <= minimumRequiredWidth) {
        placementUpdate = determineVerticalSide(innerHeight, tooltipRect, buttonRect, true);
      } else {
        placementUpdate = determineHorizontalSide(innerWidth, tooltipRect, buttonRect, false);
      }
    } else if (outerHeight <= minimumRequiredHeight) {
      placementUpdate = determineHorizontalSide(innerWidth, tooltipRect, buttonRect, true);
    } else {
      placementUpdate = determineVerticalSide(innerHeight, tooltipRect, buttonRect, false);
    }

    if (placementState !== placementUpdate) {
      setPlacementState(placementUpdate);
    }

    return placementUpdate;
  }
  /**
   * Offsets the tooltip if it is on the top or the bottom, otherwise resets offsets
   */


  var offsetTooltip = useCallback(function () {
    var toLeft = tooltipRef.current.getBoundingClientRect().left;
    var toRight = tooltipRef.current.getBoundingClientRect().right;
    var topOrBottom = placementState === 'top' || placementState === 'bottom';

    if (toLeft <= 0 && topOrBottom) {
      setOffsetCss({
        left: "".concat(-toLeft, "px")
      });
    } else if (window && window.innerWidth - toRight <= 0 && topOrBottom) {
      setOffsetCss({
        right: "".concat(-(window.innerWidth - toRight), "px")
      });
    } else {
      // Otherwise, reset the tooltip offset
      setOffsetCss({});
    }
  }, [placementState]);
  /**
   * Aligns the tooltip to a position if needed. If no changes
   * to the alignment, offsets the tooltip accordingly
   */

  function alignTooltip() {
    var alignedPlacement = alignForWindowOverflow();

    if (!alignedPlacement || alignedPlacement === placementState) {
      offsetTooltip();
    }
  }

  useResize(function () {
    alignTooltip();
  }, [tooltipRef.current, placementState]);
  useEffect(function () {
    var timeout = setTimeout(function () {
      offsetTooltip();
    }, 500);
    return function () {
      clearTimeout(timeout);
    };
  }, [tooltipRef, placementState, offsetTooltip]);
  var close = useCallback(function (event) {
    setOpenState(false);
    setManaged(false);

    if (triggerElement.props.handleTooltipToggle) {
      triggerElement.props.handleTooltipToggle(event);
    }
  }, [triggerElement]);

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      watchOutsideClick = _useState10[0],
      setWatchOutsideClick = _useState10[1];

  var onWindowClick = useCallback(function (event) {
    if (watchOutsideClick) {
      close(event);

      if (triggerElement.props.onBlur) {
        triggerElement.props.onBlur(event);
      }
    }
  }, [close, triggerElement, watchOutsideClick]);
  useEffect(function () {
    setWatchOutsideClick(managed && openState);
  }, [managed, info, openState]);
  useClickOutside(tooltipOuterRef, onWindowClick);
  useEscape(close);
  var shouldShow = open === false ? false : open || openState;

  var _getPlacementStyles = getPlacementStyles(placementState),
      _getPlacementStyles2 = _slicedToArray(_getPlacementStyles, 3),
      tooltipPlacementCss = _getPlacementStyles2[0],
      tooltipArrowPlacementCss = _getPlacementStyles2[1],
      tooltipArrowPointerCss = _getPlacementStyles2[2];

  var eventedElement = /*#__PURE__*/React.cloneElement(triggerElement, _objectSpread(_objectSpread({
    'aria-labelledby': id
  }, !info ? {
    onMouseEnter: function onMouseEnter(event) {
      if (!managed && !openState) {
        setOpenState(true);
      }

      event.stopPropagation();

      if (triggerElement.props.onMouseEnter) {
        triggerElement.props.onMouseEnter(event);
      }
    },
    onMouseLeave: function onMouseLeave(event) {
      if (!managed) {
        setOpenState(false);
      }

      if (managed && !openState) {
        setManaged(false);
      }

      event.stopPropagation();

      if (triggerElement.props.onMouseLeave) {
        triggerElement.props.onMouseLeave(event);
      }
    }
  } : {}), {}, {
    onClick: function onClick(event) {
      if (managed || info) {
        setOpenState(function (currentOpenState) {
          return !currentOpenState;
        });
      }

      setManaged(true);
      event.stopPropagation();

      if (triggerElement.props.onClick) {
        triggerElement.props.onClick(event);
      }

      if (triggerElement.props.handleTooltipToggle) {
        triggerElement.props.handleTooltipToggle(event);
      }
    },
    active: info ? getPreferredValue(triggerElement.props.active, shouldShow) : triggerElement.props.active
  }));
  return jsx("div", _extends({
    css: _objectSpread(_objectSpread({}, tooltipContainer), theme),
    ref: tooltipOuterRef
  }, otherProps), eventedElement, jsx("div", {
    id: id,
    role: "tooltip",
    ref: tooltipRef,
    css: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, tooltip), tooltipPlacementCss), shouldShow ? tooltipAnimIn : {}), info ? tooltipInfo : {})
  }, jsx("div", {
    "aria-hidden": "true",
    css: _objectSpread(_objectSpread({}, tooltipStyleObject.tooltipArrow), tooltipArrowPlacementCss)
  }, jsx("div", {
    css: _objectSpread(_objectSpread(_objectSpread({}, tooltipArrowPointer), tooltipArrowPointerCss), shouldShow ? tooltipAnimInTooltipArrowPointer : {})
  })), jsx("div", {
    css: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, tooltipInner), shouldShow ? tooltipAnimInTooltipInner : {}), info ? tooltipInfoInner : notTooltipInfoAndTooltipInner), offsetCss)
  }, children)));
}

Tooltip.defaultProps = {
  theme: undefined,
  info: false,
  placement: 'top',
  forcePlacement: undefined,
  open: undefined
};

export default Tooltip;
